<script setup>
import { reactive, ref, shallowRef, watch } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { matches, required, strongPassword } from '@/utils/validation';

// Admin sets a temporary password for a user. It isn't emailed: the admin
// shares it, and the user chooses their own when they next sign in.
const props = defineProps({
  account: { type: Object, default: null },
});
const open = defineModel({ type: Boolean, default: false });

const toast = useToast();
const formRef = ref(null);
const saving = shallowRef(false);
const reveal = shallowRef(false);
const form = reactive({ password: '', confirm: '' });

watch(open, (isOpen) => {
  if (!isOpen) return;
  form.password = '';
  form.confirm = '';
  reveal.value = false;
  formRef.value?.resetValidation();
});

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const body = await apiRequest(
      'post',
      `/auth/changePassIDCred/${props.account.id}`,
      { old_password: null, new_password: form.password },
    );
    assertOk(body);
    toast.success(body.msg || 'Password saved.');
    open.value = false;
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the password.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    title="Set password"
    :description="account ? `Temporary password for ${account.name}. Share it with them; they'll choose their own when they next sign in.` : ''"
    size="sm"
  >
    <v-form ref="formRef" class="form-stack" @submit.prevent="save">
      <UiFormField label="New password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.password"
          :type="reveal ? 'text' : 'password'"
          :append-inner-icon="reveal ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :rules="[required, strongPassword]"
          hide-details="auto"
          autocomplete="new-password"
          @click:append-inner="reveal = !reveal"
        />
      </UiFormField>
      <UiFormField label="Confirm password" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.confirm"
          :type="reveal ? 'text' : 'password'"
          :rules="[
            required,
            matches(() => form.password, 'Passwords do not match.'),
          ]"
          hide-details="auto"
          autocomplete="new-password"
        />
      </UiFormField>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" @click="save">
        Save password
      </v-btn>
    </template>
  </UiDialog>
</template>
